/**
 * SA-1078 (UX pass spec move 3, §4, Q4): the task page's two pure rules — its
 * width, keyed on the task's content and never a flag, and the origin it goes
 * back to, read from router state and falling back to the task's record.
 */
import type { PageWidth } from '@/components/layout/PageFrame'
import { LIVE_TASK_STATUSES } from '@/components/process/taskStatusText'
import { PAGE_LOAD, type TaskOrigin } from '@/components/process/taskLink'
import type { TaskRead } from '@/types/process'

/** `detail` (1152px) on a live task whose form holds a table, or when a custom
 * task view mounts; `form` (768px) otherwise — an ended task renders no form. */
export function pageWidth(task: Pick<TaskRead, 'status' | 'form'>, hasCustomView: boolean): PageWidth {
  if (!LIVE_TASK_STATUSES.has(task.status)) return 'form'
  return hasCustomView || task.form.some((f) => f.type === 'table') ? 'detail' : 'form'
}

/** Every task carries a record, so the record is the one fallback. */
export const recordOrigin = (orderId: string): TaskOrigin => ({ to: `/orders?order=${orderId}` })

const MAX = 200
/** The most history entries back an origin may claim. */
export const MAX_BACK = 10
/** An in-app path: one leading slash, never `//` or `/\` (a scheme-less
 * authority — the URL parser reads `\` as `/` on http(s)), never a backslash
 * or a control character anywhere (the parser strips tabs and newlines, so
 * `/\t/evil.example/` is an off-site href once rendered). */
// eslint-disable-next-line no-control-regex
const PATH = /^\/(?![/\\])[^\\\u0000-\u001F\u007F]*$/

/** The origin router state holds, or `fallback` when it holds none or holds
 * something this page did not write (a reload, a shared link, a forged state). */
export function readOrigin(state: unknown, fallback: TaskOrigin): TaskOrigin {
  if (typeof state !== 'object' || state === null) return fallback
  const from = (state as { from?: unknown }).from
  if (typeof from !== 'object' || from === null) return fallback
  const { to, label, back, load } = from as { to?: unknown; label?: unknown; back?: unknown; load?: unknown }
  if (typeof to !== 'string' || to.length > MAX || !PATH.test(to)) return fallback
  if (label !== undefined && (typeof label !== 'string' || label.length > MAX)) return fallback
  const origin: TaskOrigin = label === undefined ? { to } : { to, label }
  if (typeof back === 'number' && Number.isInteger(back) && back >= 1 && back <= MAX_BACK && load === PAGE_LOAD) {
    origin.back = back
    origin.load = PAGE_LOAD
  }
  return origin
}

/** SA-1328: where Done lands when the task was opened from its own record: that
 * record's list address with the step and panel it was opened on dropped and
 * `land=process` added, or null when the origin is anywhere else. */
export function landingPath(originTo: string, orderId: string): string | null {
  const [path, query = ''] = originTo.split('?')
  if (path !== '/orders') return null
  const params = new URLSearchParams(query)
  if (params.get('order') !== orderId) return null
  params.delete('step'); params.delete('panel'); params.delete('land')
  params.append('land', 'process')
  return `${path}?${params.toString()}`
}
